import { type Database } from "@fitness-app/supabase";

import { type AppReleasePlatform } from "./AppRelease";

export type SupportRequestEntity = Database["public"]["Tables"]["support_request"]["Row"];
export type SupportRequestInsert = Database["public"]["Tables"]["support_request"]["Insert"];
export type SupportRequestUpdate = Database["public"]["Tables"]["support_request"]["Update"];

export type SupportRequestCommentEntity = Database["public"]["Tables"]["support_request_comment"]["Row"];
export type SupportRequestCommentInsert = Database["public"]["Tables"]["support_request_comment"]["Insert"];

// Values mirror the DB check() constraint on support_request.status (board columns).
export enum SupportRequestStatus {
  Reported = "reported",
  NeedsInfo = "needs_info",
  Accepted = "accepted",
  InProgress = "in_progress",
  Qa = "qa",
  Done = "done",
  Rejected = "rejected",
}

// Polish board column labels — the admin board and the server-side Slack thread
// updates must describe statuses with the same words.
export const SUPPORT_REQUEST_STATUS_LABELS: Record<SupportRequestStatus, string> = {
  [SupportRequestStatus.Reported]: "Zgłoszenia",
  [SupportRequestStatus.NeedsInfo]: "Do wyjaśnienia",
  [SupportRequestStatus.Accepted]: "Przyjęte",
  [SupportRequestStatus.InProgress]: "W trakcie",
  [SupportRequestStatus.Qa]: "QA - Testy",
  [SupportRequestStatus.Done]: "Zamknięte",
  [SupportRequestStatus.Rejected]: "Odrzucone",
};

// Source relation stored on support_request.metadata (chat message / Slack thread).
export interface SupportRequestMetadata {
  chatMessageId?: string;
  chatChannelId?: string;
  slackChannelId?: string;
  slackThreadTs?: string;
  slackPermalink?: string;
}

export enum SupportRequestSource {
  Admin = "admin",
  Slack = "slack",
  AgentChat = "agent_chat",
  TrelloImport = "trello_import",
}

export enum SupportRequestCommentAuthorType {
  User = "user",
  Agent = "agent",
}

export interface SupportRequestAttachment {
  name: string;
  url: string;
  mimeType?: string;
  // Original source URL when imported (e.g. Trello attachment).
  sourceUrl?: string;
}

export interface SupportRequestTriageResolution {
  outcome: "approved" | "rejected";
  byUserId: string;
  at: string;
}

export interface SupportRequestTriage {
  analyzedAt: string;
  summary: string;
  proposedStatus: SupportRequestStatus.Accepted | SupportRequestStatus.NeedsInfo;
  questions?: string[];
  // Set when the proposal is approved/rejected in the admin drawer (TRE-1521).
  resolution?: SupportRequestTriageResolution;
}

// One app release containing the fix — a request may list several (e.g. the same
// fix shipping in the admin panel and both mobile apps).
export interface SupportRequestFixedInVersion {
  platform: AppReleasePlatform;
  version: string;
}

export type SupportRequest = Omit<SupportRequestEntity, "attachments" | "triage" | "tags" | "fixedInVersions"> & {
  status: SupportRequestStatus;
  source: SupportRequestSource;
  attachments: SupportRequestAttachment[];
  triage: SupportRequestTriage | null;
  // Predefined values only — the allowed list lives in trainer_config.supportRequestTags.
  tags: string[];
  // Releases the fix shipped in; the pickable list lives in app_release (auto-recorded
  // on /app-config saves).
  fixedInVersions: SupportRequestFixedInVersion[];
};

// Links a comment to the action it was written for — currently the close/reject
// modal records the status transition (TRE-1765 #4).
export interface SupportRequestCommentMetadata {
  statusChange?: {
    from: SupportRequestStatus;
    to: SupportRequestStatus;
  };
}

export type SupportRequestComment = SupportRequestCommentEntity & {
  authorType: SupportRequestCommentAuthorType;
};
